// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Resource Filtering Operations
 * 
 * This module provides the core filtering logic for resources, including
 * default resource filtering and custom filter operations (prefix, keyword).
 */

import { 
  exactNamesSet, 
  prefixes, 
  keywords, 
  prefixesSet, 
  keywordsSet 
} from './Dictionaries/AutoGeneratedResourceFilterData';

function sanitizeInput(input) {
  if (typeof input !== 'string') return '';
  return input.replace(/[<>'"&]/g, '');
}

function processFilterValues(keywordString) {
  if (!keywordString?.trim()) return [];
  
  return parseCommaSeparatedValues(keywordString)
    .map(k => sanitizeInput(k.toLowerCase()))
    .filter(k => k.length > 0);
}

function validateFilterInputs(resources, filterValue) {
  if (!Array.isArray(resources)) {
    return null;
  }
  
  const processedValues = processFilterValues(filterValue);
  return processedValues.length === 0 ? null : processedValues;
}

export function getResourceName(resource) {
  return resource?.properties?.resourceName || 
         resource?.properties?.title || 
         resource?.properties?.resourceId || 
         '';
}

export function shouldExcludeResource(resourceName) {
  if (!resourceName) return false;
  
  const nameLower = resourceName.toLowerCase();
  
  if (exactNamesSet.has(nameLower)) {
    return true;
  }

  if (prefixesSet.some(prefix => nameLower.startsWith(prefix))) {
    return true;
  }

  if (keywordsSet.some(keyword => nameLower.includes(keyword))) {
    return true;
  }
  
  return false;
}

export function filterDefaultResources(resources) {
  if (!Array.isArray(resources)) {
    return [];
  }
  
  return resources.filter(resource => {
    const resourceName = getResourceName(resource);
    return !shouldExcludeResource(resourceName);
  });
}

export function applyPrefixFilter(resources, prefixValue) {
  const prefixFilters = validateFilterInputs(resources, prefixValue);
  if (!prefixFilters) {
    return resources;
  }
  
  // EXCLUDE resources that contain any of the keywords (not just startsWith)
  return resources.filter(resource => {
    const name = getResourceName(resource).toLowerCase();
    return !prefixFilters.some(keyword => name.includes(keyword));
  });
}

export function applyKeywordFilter(resources, keywordValue) {
  const keywordFilters = validateFilterInputs(resources, keywordValue);
  if (!keywordFilters) {
    return resources;
  }
  
  // INCLUDE resources that contain at least one of the keywords
  return resources.filter(resource => {
    const name = getResourceName(resource).toLowerCase();
    return keywordFilters.some(keyword => name.includes(keyword));
  });
}

export function applyFilterToken(resources, token) {
  if (!Array.isArray(resources)) {
    return [];
  }
  
  if (!token?.propertyKey) {
    return resources;
  }
  
  switch (token.propertyKey) {
    case 'resourceType':
      return token.value === 'auto-generated' && token.operator === '!=' 
        ? filterDefaultResources(resources)
        : resources;
      
    case 'prefix':
      return applyPrefixFilter(resources, token.value);
      
    case 'keyword':
      return applyKeywordFilter(resources, token.value);
      
    default:
      return resources;
  }
}

export function applyFilters(resources, filteringQuery) {
  if (!Array.isArray(resources)) {
    return [];
  }
  
  if (!filteringQuery?.tokens || !Array.isArray(filteringQuery.tokens)) {
    return resources;
  }
  
  return filteringQuery.tokens.reduce(
    (filteredResources, token) => applyFilterToken(filteredResources, token),
    resources
  );
}

export function parseCommaSeparatedValues(value) {
  if (!value || typeof value !== 'string') {
    return [];
  }
  
  return value
    .split(',')
    .map(v => v.trim())
    .filter(v => v.length > 0);
}

/**
 * View preset identifiers for Create Account Diagram.
 */
export const VIEW_PRESET_TYPES = {
    FULL: 'full view',
    BUILDER: 'builder view', 
    SECURITY: 'security engineer view'
};

/**
 * AWS resource type constants for improved maintainability.
 */
const AWS_LAMBDA_FUNCTION = 'AWS::Lambda::Function';
const AWS_ECS_SERVICE = 'AWS::ECS::Service';
const AWS_ECS_CLUSTER = 'AWS::ECS::Cluster';
const AWS_ECS_TASKDEFINITION = 'AWS::ECS::TaskDefinition';
const AWS_EKS_CLUSTER = 'AWS::EKS::Cluster';
const AWS_EKS_NODEGROUP = 'AWS::EKS::Nodegroup';
const AWS_EC2_INSTANCE = 'AWS::EC2::Instance';
const AWS_AUTOSCALING_GROUP = 'AWS::AutoScaling::AutoScalingGroup';
const AWS_ELB_V2_LOADBALANCER = 'AWS::ElasticLoadBalancingV2::LoadBalancer';
const AWS_ELB_LOADBALANCER = 'AWS::ElasticLoadBalancing::LoadBalancer';
const AWS_APIGATEWAY_RESTAPI = 'AWS::ApiGateway::RestApi';
const AWS_APIGATEWAY_V2_API = 'AWS::ApiGatewayV2::Api';
const AWS_CLOUDFRONT_DISTRIBUTION = 'AWS::CloudFront::Distribution';
const AWS_RDS_DBINSTANCE = 'AWS::RDS::DBInstance';
const AWS_RDS_DBCLUSTER = 'AWS::RDS::DBCluster';
const AWS_DYNAMODB_TABLE = 'AWS::DynamoDB::Table';
const AWS_S3_BUCKET = 'AWS::S3::Bucket';
const AWS_OPENSEARCH_DOMAIN = 'AWS::OpenSearchService::Domain';
const AWS_NEPTUNE_DBCLUSTER = 'AWS::Neptune::DBCluster';
const AWS_SQS_QUEUE = 'AWS::SQS::Queue';
const AWS_SNS_TOPIC = 'AWS::SNS::Topic';
const AWS_EVENTS_RULE = 'AWS::Events::Rule';
const AWS_EVENTBRIDGE_RULE = 'AWS::EventBridge::Rule';
const AWS_IAM_ROLE = 'AWS::IAM::Role';
const AWS_IAM_USER = 'AWS::IAM::User';
const AWS_IAM_POLICY = 'AWS::IAM::Policy';
const AWS_IAM_GROUP = 'AWS::IAM::Group';
const AWS_IAM_INSTANCEPROFILE = 'AWS::IAM::InstanceProfile';
const AWS_EC2_VPC = 'AWS::EC2::VPC';
const AWS_EC2_SECURITYGROUP = 'AWS::EC2::SecurityGroup';
const AWS_EC2_SUBNET = 'AWS::EC2::Subnet';
const AWS_EC2_ROUTETABLE = 'AWS::EC2::RouteTable';
const AWS_EC2_NATGATEWAY = 'AWS::EC2::NatGateway';
const AWS_EC2_INTERNETGATEWAY = 'AWS::EC2::InternetGateway';
const AWS_EC2_VPCENDPOINT = 'AWS::EC2::VPCEndpoint';
const AWS_EC2_NETWORKACL = 'AWS::EC2::NetworkAcl';
const AWS_ROUTE53_HOSTEDZONE = 'AWS::Route53::HostedZone';
const AWS_ROUTE53_RESOLVER_ENDPOINT = 'AWS::Route53Resolver::ResolverEndpoint';
const AWS_CLOUDWATCH_LOGGROUP = 'AWS::CloudWatch::LogGroup';
const AWS_SECRETS_MANAGER_SECRET = 'AWS::SecretsManager::Secret';
const AWS_KMS_KEY = 'AWS::KMS::Key';
const AWS_SSM_PARAMETER = 'AWS::SSM::Parameter';
const AWS_BEDROCK_AGENT = 'AWS::Bedrock::Agent';
const AWS_BEDROCK_KNOWLEDGEBASE = 'AWS::Bedrock::KnowledgeBase';
const AWS_DOCUMENTDB_DBCLUSTER = 'AWS::DocumentDB::DBCluster';
const AWS_CLOUDTRAIL_TRAIL = 'AWS::CloudTrail::Trail';
const AWS_CONFIG_RECORDER = 'AWS::Config::ConfigurationRecorder';
const AWS_CONFIG_DELIVERY_CHANNEL = 'AWS::Config::DeliveryChannel';
const AWS_GUARDDUTY_DETECTOR = 'AWS::GuardDuty::Detector';
const AWS_WAF_WEBACL = 'AWS::WAF::WebACL';
const AWS_WAFV2_WEBACL = 'AWS::WAFv2::WebACL';

/**
 * View preset resource type definitions for Create Account Diagram optimization.
 * Provides curated resource selections for common architectural analysis workflows.
 */
const VIEW_PRESET_RESOURCE_TYPES = {
    builder: [
        AWS_LAMBDA_FUNCTION, AWS_ECS_SERVICE, AWS_ECS_CLUSTER, AWS_ECS_TASKDEFINITION,
        AWS_EKS_CLUSTER, AWS_EKS_NODEGROUP, AWS_EC2_INSTANCE, AWS_AUTOSCALING_GROUP,
        AWS_ELB_V2_LOADBALANCER, AWS_ELB_LOADBALANCER,
        AWS_APIGATEWAY_RESTAPI, AWS_APIGATEWAY_V2_API, AWS_CLOUDFRONT_DISTRIBUTION,
        AWS_RDS_DBINSTANCE, AWS_RDS_DBCLUSTER, AWS_DYNAMODB_TABLE, AWS_S3_BUCKET,
        AWS_OPENSEARCH_DOMAIN, AWS_NEPTUNE_DBCLUSTER, AWS_SQS_QUEUE, AWS_SNS_TOPIC,
        AWS_EVENTS_RULE, AWS_EVENTBRIDGE_RULE, AWS_IAM_ROLE, AWS_EC2_VPC,
        AWS_EC2_SECURITYGROUP, AWS_EC2_SUBNET, AWS_EC2_ROUTETABLE, AWS_EC2_NATGATEWAY,
        AWS_EC2_INTERNETGATEWAY, AWS_EC2_VPCENDPOINT, AWS_ROUTE53_HOSTEDZONE, AWS_CLOUDWATCH_LOGGROUP,
    ],
    security: [
        AWS_IAM_ROLE, AWS_IAM_USER, AWS_IAM_POLICY, AWS_IAM_GROUP, AWS_IAM_INSTANCEPROFILE,
        AWS_LAMBDA_FUNCTION, AWS_EC2_INSTANCE, AWS_ECS_SERVICE, AWS_ECS_CLUSTER, AWS_EKS_CLUSTER,
        AWS_ELB_V2_LOADBALANCER, AWS_ELB_LOADBALANCER,
        AWS_APIGATEWAY_RESTAPI, AWS_APIGATEWAY_V2_API, AWS_CLOUDFRONT_DISTRIBUTION, AWS_ROUTE53_HOSTEDZONE,
        AWS_ROUTE53_RESOLVER_ENDPOINT, AWS_SQS_QUEUE, AWS_SNS_TOPIC, AWS_EVENTS_RULE,
        AWS_EVENTBRIDGE_RULE, AWS_SECRETS_MANAGER_SECRET, AWS_KMS_KEY, AWS_SSM_PARAMETER,
        AWS_BEDROCK_AGENT, AWS_BEDROCK_KNOWLEDGEBASE, AWS_EC2_VPC, AWS_EC2_SUBNET,
        AWS_EC2_SECURITYGROUP, AWS_EC2_NETWORKACL, AWS_EC2_INTERNETGATEWAY, AWS_EC2_NATGATEWAY,
        AWS_EC2_VPCENDPOINT, AWS_S3_BUCKET, AWS_DYNAMODB_TABLE, AWS_RDS_DBINSTANCE,
        AWS_RDS_DBCLUSTER, AWS_DOCUMENTDB_DBCLUSTER, AWS_OPENSEARCH_DOMAIN,
        AWS_CLOUDTRAIL_TRAIL, AWS_CONFIG_RECORDER, AWS_CONFIG_DELIVERY_CHANNEL,
        AWS_GUARDDUTY_DETECTOR, AWS_WAF_WEBACL, AWS_WAFV2_WEBACL,
    ]
};

/**
 * Returns AWS resource types for specified view preset.
 * @param {string} viewType - View preset identifier ('builder' or 'security')
 * @returns {string[]} Array of AWS resource type identifiers
 */
export function getResourceTypesForViewPreset(viewType) {
    return VIEW_PRESET_RESOURCE_TYPES[viewType] || [];
}

/**
 * Filters available resource type options by view preset selection.
 * @param {string} viewType - View preset identifier  
 * @param {Object[]} availableResourceTypes - Complete resource type option array
 * @returns {Object[]} Filtered resource type options for multiselect component
 */
export function filterResourceTypesByViewPreset(viewType, availableResourceTypes) {
    if (!viewType || !availableResourceTypes) {
        return availableResourceTypes || [];
    }
    
    const presetTypes = getResourceTypesForViewPreset(viewType);
    return availableResourceTypes.filter(option => presetTypes.includes(option.value));
}
